import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { BoardDetailMenu } from '@/components/Board/BoardDetail/styles';
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { Section, WorkHoursTable } from './styles';
import { SubContentProps } from '../LanguageSetting';
import { useTranslation } from 'react-i18next';
import {
  WORKING_HOURS_OPTIONS,
  joinWorkingHours,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkingHoursChange/@hooks/useWorkingHoursChangeProps';
import useSWR from 'swr';
import { getNestedDataFetcher } from '@/utils/apis';
import { fetchApi } from '@/utils/api/customAxios';
import { getApprovalDocUrl, getIntranetDocUrl } from '@/utils/urls';
import { useDialog } from '@/hooks/global-tools';
import { useSnackbar } from '@/hooks/global-tools';
import { AxiosError } from 'axios';
import { PostMessageActionType, sendPostMessage } from '@/utils/postMessage';
import WorkingHoursChangeModal from './WorkingHoursChangeModal';

type WorkingHoursHistory = {
  id: number;
  passportId: string;
  workingStartHour: string;
  workingEndHour: string;
  workingHours: string | null;
  approvalId: string;
  changedAt: string;
  createdAt: string;
  updatedAt: string;
};

const getWorkingHoursLabel = (item: WorkingHoursHistory): string => {
  const value =
    item.workingStartHour && item.workingEndHour
      ? joinWorkingHours(item.workingStartHour, item.workingEndHour)
      : item.workingHours ?? '';
  return (
    WORKING_HOURS_OPTIONS.find((opt) => opt.value === value)?.label ?? value
  );
};

const formatMonthDay = (dateStr: string) => {
  const datePart = dateStr.split('T')[0];
  const [, m, d] = datePart.split('-');
  return `${String(Number(m)).padStart(2, '0')}월 ${String(Number(d)).padStart(
    2,
    '0',
  )}일`;
};

const WorkHours = ({ openSubMenu, menuOnClose, myInfo }: SubContentProps) => {
  const { t } = useTranslation();
  const [isChangeDrawerOpen, setIsChangeDrawerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuApprovalId, setMenuApprovalId] = useState<string | null>(null);
  const [menuItemCancellable, setMenuItemCancellable] = useState(false);
  const dialog = useDialog();
  const { showSuccess } = useSnackbar();

  const handleApprovalNavigation = (url: string) => {
    if (window?.isRNWebview) {
      sendPostMessage(PostMessageActionType.MOVE_TAB, {
        tabName: 'approval',
        url,
      });
    } else {
      window.open(url, '_blank', 'noreferrer');
    }
  };

  const currentWorkHours =
    WORKING_HOURS_OPTIONS.find((opt) => opt.value === myInfo.workingHours)
      ?.label ?? myInfo.workingHours;

  const { data: historyData, mutate: mutateHistory } = useSWR<
    WorkingHoursHistory[]
  >(`/api/v1/passport/me/working-hours/histories`, getNestedDataFetcher);

  const handleKebabOpen = (
    event: React.MouseEvent<HTMLButtonElement>,
    approvalId: string | null,
    isCancellable: boolean,
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuApprovalId(approvalId);
    setMenuItemCancellable(isCancellable);
  };

  const handleKebabClose = () => {
    setMenuAnchor(null);
    setMenuApprovalId(null);
    setMenuItemCancellable(false);
  };

  const handleCancelRequest = () => {
    const approvalId = menuApprovalId;
    handleKebabClose();
    dialog.show({
      type: 'confirm',
      message: '근무시간 변경 신청을 취소하시겠습니까?',
      confirmButtonText: '취소하기',
      cancelButtonText: '유지하기',
      onConfirm: async () => {
        if (!approvalId) return;
        try {
          await fetchApi({
            method: 'DELETE',
            url: `/api/v1/passport/working-hours/${approvalId}`,
          });
          mutateHistory();
          showSuccess({ message: '취소가 완료되었습니다.' });
        } catch (e) {
          const status = e instanceof AxiosError ? e.response?.status : null;
          if (status !== 422) {
            dialog.show({
              type: 'alert',
              message: '취소 처리 중 오류가 발생했습니다.',
            });
          }
        }
      },
    });
  };

  return (
    <>
      {/* 현재 근무 시간 */}
      <Section sx={{ padding: '12px 16px 12px 20px' }}>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            color: 'var(--dk-text, #000)',
          }}
        >
          현재 근무 시간은{' '}
          <Box
            component="span"
            sx={{
              color: 'var(--dk-accent, #06C)',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px',
            }}
          >
            {currentWorkHours}
          </Box>{' '}
          입니다.
        </Typography>
      </Section>

      <div
        style={{ width: '100%', height: '8px', backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)' }}
      ></div>
      {/* 근무 시간 변경 내역 */}
      <Section sx={{ padding: '24px 20px 0' }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            mb: '16px',
            alignItems: 'center',
          }}
        >
          <Stack flex={1} gap={'4px'}>
            <Typography
              sx={{
                color: 'var(--dk-text, #000)',
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '24px',
              }}
            >
              근무 시간 변경 내역
            </Typography>
            <Typography
              sx={{
                color: 'var(--dk-text-secondary, #888D96)',
                fontSize: '14px',
                fontWeight: 400,
                lineHeight: '18px',
              }}
            >
              근무 시간은 월 1회 변경을 원칙으로 합니다.
            </Typography>
          </Stack>
        </Box>

        <WorkHoursTable>
          <thead>
            <tr>
              <th>신청일</th>
              <th>변경일</th>
              <th>근무 시간</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {historyData && historyData.length > 0 ? (
              [...(historyData ?? [])]
                .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                .map((item) => (
                  <tr key={item.id}>
                    <td
                      className={item.approvalId ? 'link' : undefined}
                      onClick={() =>
                        handleApprovalNavigation(
                          window?.isRNWebview
                            ? getIntranetDocUrl(item.approvalId, false)
                            : getApprovalDocUrl(item.approvalId, false),
                        )
                      }
                    >
                      {formatMonthDay(item.createdAt)}
                    </td>
                    <td>{formatMonthDay(item.changedAt)}</td>
                    <td>{getWorkingHoursLabel(item)}</td>
                    <td className="action">
                      <IconButton
                        size="small"
                        onClick={(e) =>
                          handleKebabOpen(
                            e,
                            item.approvalId,
                            !!item.approvalId &&
                              new Date(item.changedAt) > new Date(),
                          )
                        }
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    textAlign: 'center',
                    padding: '16px 0',
                    color: 'var(--dk-text-secondary, #888D96)',
                  }}
                >
                  변경 내역이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </WorkHoursTable>

        <BoardDetailMenu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleKebabClose}
          MenuListProps={{ sx: { width: '160px' } }}
        >
          <Tooltip
            title={
              !menuItemCancellable
                ? '이미 적용된 신청건은 취소가 불가능합니다.'
                : ''
            }
            placement="top"
          >
            <span style={{ display: 'block' }}>
              <MenuItem
                disabled={!menuItemCancellable}
                style={{ color: !menuItemCancellable ? '#ccc' : 'red' }}
                sx={{
                  padding: '6px',
                  '& .MuiTouchRipple-root': { color: 'var(--dk-text, black)' },
                }}
                onClick={handleCancelRequest}
              >
                취소하기
              </MenuItem>
            </span>
          </Tooltip>
        </BoardDetailMenu>
      </Section>

      {/* 근무 시간 변경 신청 버튼 */}
      <Section sx={{ padding: '16px 20px 20px' }}>
        <Button
          variant="outlined"
          fullWidth
          size="large"
          onClick={() => setIsChangeDrawerOpen(true)}
          sx={{
            borderColor: 'var(--dk-line, #E7E9EC)',
            color: 'var(--dk-text, #000)',
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '24px',
            borderRadius: '8px',
            height: '48px',
          }}
        >
          {t('MYPAGE.근무 시간 변경 신청')}
        </Button>
      </Section>

      <WorkingHoursChangeModal
        open={isChangeDrawerOpen}
        onClose={() => setIsChangeDrawerOpen(false)}
        myInfo={myInfo}
        menuOnClose={menuOnClose}
        openSubMenu={openSubMenu}
      />
    </>
  );
};

export default WorkHours;
